import { action, observable, runInAction, makeObservable } from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IDrawerContent, IDrawerContentHandler, IDrawer} from "./drawer.service.interface";
import {DrawerContentDecorator} from './drawer-content-decorator';

export class Drawer implements IDrawer {

  @observable.ref
  private _drawerContentStack: IDrawerContent[] = [];

  constructor(private _services:IServiceFactoryExtended){
    makeObservable(this);
  }

  getOverlayZIndex(): number {
    const itemIndex = this.getLastVisibleDrawerItemIndex();
    const drawerItem = this.currentContent && this.currentContent[itemIndex];
    if(drawerItem?.minOverlayZIndex) {
      return drawerItem.minOverlayZIndex;
    }

    return 110;
  }

  /**
   * The drawer can contain multiple items but only the last one added will actually be visible on the screen.
   * However if the last one has isMinimized=true then the one added before will be rendered and so on.
   * Basically this method returns the index of the item that will be rendered as visible in the drawer.
   * There might be other items rendered in the drawer but those will have display: none
   */
   getLastVisibleDrawerItemIndex(): number {
    const currentDrawerContent = this.currentContent;
    for(let i = currentDrawerContent.length - 1; i>=0; i--) {
      if(!currentDrawerContent[i].isMinimized) {
        return i;
      }
    }

    return -1;
  }


  @action.bound
  push(content: IDrawerContent): IDrawerContentHandler {

    content = new DrawerContentDecorator(content, this._services.tools.getGuid());

    const existingContentIndex = this._drawerContentStack.findIndex(c => c.id === content.id)
    if(existingContentIndex >= 0) {
      this._drawerContentStack.splice(existingContentIndex, 1); // if is already in the drawer we will remove it here in order to put it on top in the next line
    }

    this._drawerContentStack = [...this._drawerContentStack, content];

    return {
      remove: () => {
        const contentIndexToRemove = this._drawerContentStack.findIndex(c => c.id === content.id);
        if(contentIndexToRemove >= 0) {
          this._removeOneDrawerItems(contentIndexToRemove);
        }
      }
    }
  }

  private _removeOneDrawerItems(itemIndex: number): void {
     runInAction(() => {
       const clone = [...this._drawerContentStack];
       clone.splice(itemIndex, 1);
       this._drawerContentStack = clone;
     });

  }

  @action.bound
  back() {
    const itemOnTheStackTop = this._drawerContentStack[this._drawerContentStack.length - 1]
    this._closeDrawerContentItem(itemOnTheStackTop);
    if(!itemOnTheStackTop.isMinimized) {
      this._removeOneDrawerItems(this._drawerContentStack.length - 1);
    }
  }

  get currentContent(): IDrawerContent[] {
    return this._drawerContentStack;
  }

  get isOpen(): boolean{
    return this._getNonMinimizedContents().length > 0;
  }

  get shouldShowBackButton(): boolean {
    return 1 < this.currentContent.filter(item => !item.isMinimized).length;
  }
  _getNonMinimizedContents(): IDrawerContent[] {
     return this.currentContent.filter(c => !c.isMinimized);
  }

  private _closeDrawerContentItem(item: IDrawerContent) {
    if(item.onClose){
      try {
        item.onClose();
      } catch(err){
        this._services.logger.error(`Fail to call onClose for drawer content ${item}`, err)
      }
    }
  }

  onOverlayClicked() {
     this._drawerContentStack.forEach(item => {
       item.onOverlayClicked();
     })
  }

  @action.bound
  closeDrawer(): void {
    const nonClosableItems: IDrawerContent[] = [];

    this._drawerContentStack.forEach(drawerContent => {
      if(drawerContent.supportsCloseIntent) {
        nonClosableItems.push(drawerContent);
      } else {
        this._closeDrawerContentItem(drawerContent);
        if(drawerContent.isMinimized) {
          nonClosableItems.push(drawerContent);
        }
      }
    });
    this._drawerContentStack = nonClosableItems;
  }
}
